const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const GENERIC_NAME_TOKENS = new Set([
  "admin",
  "candidate",
  "client",
  "com",
  "gmail",
  "host",
  "interview",
  "interviewer",
  "mail",
  "newzenalpha",
  "participant",
  "searchjob",
]);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const getSpeechRecognition = () => window.SpeechRecognition || window.webkitSpeechRecognition;

export const resolveSpeechRecognitionLanguage = () => {
  if (typeof navigator === "undefined") {
    return "en-US";
  }
  const browserLang = String(navigator.language || "").trim();
  if (browserLang) {
    return browserLang;
  }
  return "en-US";
};

/** Prefer the longer phrase when one transcript extends the other. */
export const pickRicherTranscript = (left, right) => {
  const a = String(left || "").trim();
  const b = String(right || "").trim();
  if (!a) return b;
  if (!b) return a;
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  if (b.length > a.length && bLower.includes(aLower)) return b;
  if (a.length > b.length && aLower.includes(bLower)) return a;
  return b.length >= a.length ? b : a;
};

export const getRoleSpeakerLabel = (role) => {
  const normalizedRole = String(role || "").trim().toLowerCase();
  if (["admin", "host", "interviewer"].includes(normalizedRole)) {
    return "Interviewer";
  }
  if (normalizedRole === "candidate") {
    return "Candidate";
  }
  return "";
};

const addNameToken = (tokens, token) => {
  const safeToken = String(token || "").replace(/[^a-z]/gi, "").toLowerCase();
  if (safeToken.length >= 3 && !GENERIC_NAME_TOKENS.has(safeToken)) {
    tokens.add(safeToken);
  }
};

const getNameTokens = (...values) => {
  const tokens = new Set();
  values.filter(Boolean).forEach((value) => {
    const text = String(value);
    text.replace(EMAIL_PATTERN, (email) => {
      const localPart = email.split("@")[0] || "";
      localPart.split(/[._+\-\s]+/).forEach((part) => addNameToken(tokens, part));
      return email;
    });
    text.split(/[._@+\-\s]+/).forEach((part) => addNameToken(tokens, part));
  });
  return Array.from(tokens);
};

export const redactTranscriptText = (text, contextValues = []) => {
  let safeText = String(text || "");
  safeText = safeText.replace(EMAIL_PATTERN, "[Email]");
  safeText = safeText.replace(/\b(my name is|i am|i'm)\s+([A-Z][A-Za-z]{2,})\b/gi, "$1 [Name]");
  getNameTokens(...contextValues).forEach((token) => {
    safeText = safeText.replace(new RegExp(`\\b${escapeRegExp(token)}\\b`, "gi"), "[Name]");
  });
  return safeText;
};

const isEmailLikeDisplayName = (value) => {
  const clean = String(value || "").trim();
  if (!clean.includes("@")) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean);
};

export const getDisplaySpeakerLabel = (segment, index) => {
  const displayName = String(
    segment?.speaker_display_name || segment?.speaker_name || ""
  ).trim();
  const normalizedName = displayName.toLowerCase();
  const roleLabel =
    segment?.speaker_label || getRoleSpeakerLabel(segment?.speaker_role);
  const genericNames = new Set(["candidate", "admin", "host", "interviewer", "speaker", "guest"]);
  const usableName =
    displayName &&
    !genericNames.has(normalizedName) &&
    !isEmailLikeDisplayName(displayName);

  if (usableName) {
    return roleLabel ? `${displayName} · ${roleLabel}` : displayName;
  }

  if (roleLabel) return roleLabel;
  if (normalizedName === "candidate") return "Candidate";
  if (["admin", "host", "interviewer"].includes(normalizedName)) return "Interviewer";
  if (displayName) return displayName;

  return `Speaker ${index + 1}`;
};

export const getParticipantRoleFromIdentity = (identity = "") => {
  const normalizedIdentity = String(identity || "").trim().toLowerCase();
  if (normalizedIdentity.startsWith("candidate-")) return "candidate";
  if (normalizedIdentity.startsWith("host-")) return "host";
  if (normalizedIdentity.startsWith("interviewer-")) return "interviewer";
  return "";
};

export const normalizeTranscriptSpeakerRole = (role = "") => {
  const normalizedRole = String(role || "").trim().toLowerCase();
  if (normalizedRole === "candidate") return "candidate";
  if (["admin", "host", "interviewer"].includes(normalizedRole)) {
    return normalizedRole === "admin" ? "interviewer" : normalizedRole;
  }
  return normalizedRole || "speaker";
};

export const parseParticipantMetadata = (metadata) => {
  try {
    return JSON.parse(metadata || "{}");
  } catch {
    return {};
  }
};
